/**
 * User PWA root. Bootstraps auth from persisted token, mounts router.
 */
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ReactElement, useEffect, useState } from 'react';
import { api } from './api/client';
import { useAuthStore } from './store/auth';
import { MobileShell } from './layout/MobileShell';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { RegisterMember } from './pages/RegisterMember';
import { Wallet } from './pages/Wallet';
import { OtcTrading } from './pages/OtcTrading';
import { OtcMyTrades } from './pages/OtcMyTrades';
import { OtcMyTradeDetail } from './pages/OtcMyTradeDetail';
import { OpenSubAccount } from './pages/OpenSubAccount';
import { UoTradingHub } from './pages/UoTradingHub';
import { UoTradingHall } from './pages/UoTradingHall';
import { UoTradingHallDetail } from './pages/UoTradingHallDetail';
import { EpHub } from './pages/EpHub';
import { News } from './pages/News';
import { Me } from './pages/Me';
import { Tickets } from './pages/Tickets';
import { Donations } from './pages/Donations';
import { DonationRecordsPage } from './pages/DonationRecordsPage';
import { EpPricing } from './pages/EpPricing';
import { UocConvert } from './pages/UocConvert';
import { Activation } from './pages/Activation';
import { SubAccounts } from './pages/SubAccounts';
import { KycSubmit } from './pages/KycSubmit';
import { TwoFactor } from './pages/TwoFactor';
import { HonorJourney } from './pages/HonorJourney';
import { BinaryTree } from './pages/BinaryTree';
import { UoContract } from './pages/UoContract';
import { RankProgress } from './pages/RankProgress';
import { Invite } from './pages/Invite';
import { History } from './pages/History';
import { ExchangeCalc } from './pages/ExchangeCalc';
import { Staking } from './pages/Staking';
import { BuyQueuePage } from './pages/BuyQueuePage';
import { IncomeDetailsPage } from './pages/IncomeDetailsPage';
import { IncomeWeekPage } from './pages/IncomeWeekPage';
import { EpTrading } from './pages/EpTrading';
import { EpBuyPage } from './pages/EpBuyPage';
import { EpBuyHistoryPage } from './pages/EpBuyHistoryPage';
import { BindWallet } from './pages/BindWallet';
import { ConvertPage } from './pages/ConvertPage';
import { MpToOrbPage } from './pages/MpToOrbPage';
import { RpToSp3Page } from './pages/RpToSp3Page';
import { ImputationDetailsPage } from './pages/ImputationDetailsPage';
import { ForkRecordsPage } from './pages/ForkRecordsPage';
import { SellUOPage } from './pages/SellUOPage';
import { EpSellPage } from './pages/EpSellPage';
import { RechargePage } from './pages/RechargePage';
import { RechargeFc, RechargeRp, RechargeTp } from './pages/RechargeTp';
import { RechargeFcRecords, RechargeRpRecords, RechargeTpRecords } from './pages/RechargeTpRecords';
import { LineageAssetTransferPage } from './pages/LineageAssetTransferPage';
import { LineageCombinedTransferPage } from './pages/LineageCombinedTransferPage';
import { WithdrawPage } from './pages/WithdrawPage';
import { StaticUsdtPayoutPage } from './pages/StaticUsdtPayoutPage';
import { ChangePwd } from './pages/ChangePwd';
import { RenameUsernamePage } from './pages/RenameUsernamePage';
import { SecurePage } from './pages/SecurePage';
import { SettingPage } from './pages/SettingPage';
import { MyFriends } from './pages/MyFriends';
import { GlobalPlayers } from './pages/GlobalPlayers';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetSecurity } from './pages/ResetSecurity';
import { EmailVerify } from './pages/EmailVerify';
import { Support } from './pages/Support';
import { Terms } from './pages/Terms';
import { Privacy } from './pages/Privacy';
import { ProdRouteGuard } from './components/ProdRouteGuard';

function prodRoute(path: string, element: ReactElement) {
  return (
    <Route
      path={path}
      element={<ProdRouteGuard path={`/${path}`}>{element}</ProdRouteGuard>}
    />
  );
}

function RequireAuth({ children }: { children: ReactElement }) {
  const token = useAuthStore((s) => s.accessToken);
  const profile = useAuthStore((s) => s.profile);
  if (!token || !profile) return <Navigate to="/login" replace />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document
      .querySelectorAll<HTMLElement>('[data-route-scroll-container]')
      .forEach((container) => container.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
  }, [pathname]);

  return null;
}

export default function App() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const profile = useAuthStore((s) => s.profile);
  const setProfile = useAuthStore((s) => s.setProfile);
  const clear = useAuthStore((s) => s.clear);
  const [booting, setBooting] = useState<boolean>(
    Boolean(accessToken && (!profile || !profile.player_number?.trim())),
  );

  useEffect(() => {
    if (!accessToken) return;
    const needsRefresh = !profile || !profile.player_number?.trim();
    if (!needsRefresh) {
      setBooting(false);
      return;
    }
    api
      .get('/api/auth/me')
      .then((p) => setProfile(p as never))
      .catch(() => clear())
      .finally(() => setBooting(false));
  }, [accessToken, profile, setProfile, clear]);

  if (booting) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gold-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<ProdRouteGuard path="/register"><Register /></ProdRouteGuard>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-security" element={<ResetSecurity />} />
        <Route path="/email-verify" element={<ProdRouteGuard path="/email-verify"><EmailVerify /></ProdRouteGuard>} />
        <Route path="/support" element={<ProdRouteGuard path="/support"><Support /></ProdRouteGuard>} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <MobileShell />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/wallet" replace />} />
          {prodRoute('wallet', <Wallet />)}
          {prodRoute('trading', <UoTradingHall />)}
          {prodRoute('trading/hub', <UoTradingHub />)}
          {prodRoute('trading/hall', <UoTradingHall />)}
          {prodRoute('trading/hall/:id', <UoTradingHallDetail />)}
          {prodRoute('trading/orders', <OtcMyTrades />)}
          {prodRoute('trading/orders/:id', <OtcMyTradeDetail />)}
          {prodRoute('ep-pricing', <EpPricing />)}
          {prodRoute('otc', <OtcTrading />)}
          {prodRoute('ep-hub', <EpHub />)}
          {prodRoute('news', <News />)}
          {prodRoute('donations', <Donations />)}
          {prodRoute('donations/records', <DonationRecordsPage />)}
          {prodRoute('uoc', <UocConvert />)}
          {prodRoute('activation', <Activation />)}
          {prodRoute('open-sub-account', <OpenSubAccount />)}
          {prodRoute('sub-accounts', <SubAccounts />)}
          {prodRoute('kyc', <KycSubmit />)}
          {prodRoute('tickets', <Tickets />)}
          {prodRoute('2fa', <TwoFactor />)}
          {prodRoute('honor', <HonorJourney />)}
          {prodRoute('tree', <BinaryTree />)}
          {prodRoute('contract', <UoContract />)}
          {prodRoute('rank', <RankProgress />)}
          {prodRoute('invite', <Invite />)}
          {prodRoute('history', <History />)}
          {prodRoute('exchange-calc', <ExchangeCalc />)}
          {prodRoute('staking', <Staking />)}
          {prodRoute('buy-fuel', <RechargeFc />)}
          {prodRoute('recharge-fc-records', <RechargeFcRecords />)}
          {prodRoute('buy-queue', <BuyQueuePage />)}
          {prodRoute('income-details', <IncomeDetailsPage />)}
          {prodRoute('income-details/week', <IncomeWeekPage />)}
          {prodRoute('convert', <ConvertPage />)}
          {prodRoute('convert/mp-orb', <MpToOrbPage />)}
          {prodRoute('convert/rp-sp3', <RpToSp3Page />)}
          {prodRoute('sell-uo', <SellUOPage />)}
          {prodRoute('register-member', <RegisterMember />)}
          {prodRoute('me', <Me />)}
          {prodRoute('change-pwd', <ChangePwd />)}
          {prodRoute('rename-username', <RenameUsernamePage />)}
          {prodRoute('secure', <SecurePage />)}
          {prodRoute('setting', <SettingPage />)}
          {prodRoute('nearby', <MyFriends />)}
          {prodRoute('all-users', <GlobalPlayers />)}
          {prodRoute('players', <MyFriends />)}
          {prodRoute('ep-trading', <EpTrading />)}
          {prodRoute('ep-buy', <EpBuyPage />)}
          {prodRoute('ep-buy-history', <EpBuyHistoryPage />)}
          {prodRoute('ep-sell', <EpSellPage />)}
          {prodRoute('bind-wallet', <BindWallet />)}
          {prodRoute('imputation', <ImputationDetailsPage />)}
          {prodRoute('fork-records', <ForkRecordsPage />)}
          {prodRoute('recharge', <RechargePage />)}
          {prodRoute('recharge-tp', <RechargeTp />)}
          {prodRoute('recharge-tp-records', <RechargeTpRecords />)}
          {prodRoute('recharge-rp', <RechargeRp />)}
          {prodRoute('recharge-rp-records', <RechargeRpRecords />)}
          {prodRoute('lineage-transfer/tp', <LineageAssetTransferPage asset="tp" />)}
          {prodRoute('lineage-transfer/fc', <LineageAssetTransferPage asset="fc" />)}
          {prodRoute('lineage-transfer/rp', <LineageAssetTransferPage asset="rp" />)}
          {prodRoute('lineage-transfer', <LineageCombinedTransferPage />)}
          {prodRoute('withdraw', <WithdrawPage />)}
          {prodRoute('static-usdt-payout', <StaticUsdtPayoutPage />)}
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
